const prefixes = [
  // Free-form inputs
  "color",
  "dimension",
  "fontsize",
  "icon",
  "image",
  "number",
  "pixel",
  "rich",
  "text",
  // Select-based inputs (finite option sets)
  "align",
  "alignitems",
  "borderstyle",
  "cursor",
  "display",
  "flexdirection",
  "flexwrap",
  "fontstyle",
  "fontweight",
  "justifycontent",
  "objectfit",
  "objectposition",
  "overflow",
  "pointerevents",
  "position",
  "shadow",
  "textdecoration",
  "textoverflow",
  "texttransform",
  "visibility",
  "whitespace",
] as const;

export type AttrPrefix = (typeof prefixes)[number];

export interface ParsedAttr {
  prefix: AttrPrefix | null;
  name: string;
  value: string;
}

export interface ParsedDefinition {
  attrs: ParsedAttr[];
  innerHTML: string;
}

/** Turns a kebab-case attribute name into a readable label, e.g. "background-color" → "Background color" */
export function attrLabel(name: string): string {
  return name.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** Reconstructs the full attribute name from prefix + name */
function attrFullName(attr: ParsedAttr): string {
  return attr.prefix ? `${attr.prefix}:${attr.name}` : attr.name;
}

export function parseDefinition(html: string): ParsedDefinition | null {
  if (!html.trim()) {
    return null;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const el = doc.querySelector("ig-component");
  if (!el) {
    return null;
  }

  const attrs: ParsedAttr[] = [];
  for (const attr of Array.from(el.attributes)) {
    const colonIdx = attr.name.indexOf(":");
    let prefix: AttrPrefix | null = null;
    let name = attr.name;

    if (colonIdx >= 0) {
      const rawPrefix = attr.name.slice(0, colonIdx) as AttrPrefix;
      if (prefixes.includes(rawPrefix)) {
        prefix = rawPrefix;
        name = attr.name.slice(colonIdx + 1);
      }
    }

    attrs.push({ prefix, name, value: attr.value });
  }

  return { attrs, innerHTML: el.innerHTML };
}

export function rebuildDefinition(
  attrs: ParsedAttr[],
  innerHTML: string
): string {
  const attrStr = attrs
    .map((a) => `${attrFullName(a)}="${a.value.replace(/"/g, "&quot;")}"`)
    .join(" ");
  return `<ig-component ${attrStr}>${innerHTML}</ig-component>`;
}
